import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import './Chat.css';
import { useLiveAgent } from './useLiveAgent';
import { useVoiceInput } from './useVoiceInput';
import { convGet, convKeys, convRemove, convSet, convShared, convTouch, startConversationStorage } from './conversationStore';
import ProductCarousel from './ProductCarousel';

marked.setOptions({ gfm: true, breaks: true, headerIds: false });

// Everything that reaches innerHTML goes through here: bot/agent replies,
// server-controlled config strings and visitor echoes alike. Mirrors the
// widget's sanitizedMarkdown (both libraries are bundled, no runtime CDN).
const sanitizeHtml = (html) => DOMPurify.sanitize(String(html ?? ''), { ADD_ATTR: ['target', 'rel'] });
const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;')
  .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

// Headset SVG (FontAwesome) used in the live-agent CTA / menu item
const HEADSET_SVG = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 512 512"
    width="16"
    height="16"
    fill="currentColor"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M256 48C141.1 48 48 141.1 48 256v40c0 13.3-10.7 24-24 24S0 309.3 0 296V256C0 114.6 114.6 0 256 0S512 114.6 512 256V400.1c0 48.6-39.4 88-88.1 88L313.6 488c-8.3 14.3-23.8 24-41.6 24h-32c-26.5 0-48-21.5-48-48s21.5-48 48-48h32c17.8 0 33.3 9.7 41.6 24l110.4 .1c22.1 0 40-17.9 40-40V256c0-114.9-93.1-208-208-208zM144 208h16c17.7 0 32 14.3 32 32V352c0 17.7-14.3 32-32 32H144c-35.3 0-64-28.7-64-64V272c0-35.3 28.7-64 64-64zm224 0c35.3 0 64 28.7 64 64v48c0 35.3-28.7 64-64 64H352c-17.7 0-32-14.3-32-32V240c0-17.7 14.3-32 32-32h16z" />
  </svg>
);

const FONT_SOURCES = {
  'inter, sans-serif': 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap',
  'roboto, sans-serif': 'https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap',
  'open sans, sans-serif': 'https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600;700&display=swap',
  'poppins, sans-serif': 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600&display=swap',
  'montserrat, sans-serif': 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600&display=swap',
  'lato, sans-serif': 'https://fonts.googleapis.com/css2?family=Lato:wght@400;700&display=swap',
  'nunito, sans-serif': 'https://fonts.googleapis.com/css2?family=Nunito:wght@400;600&display=swap',
  'source sans 3, sans-serif': 'https://fonts.googleapis.com/css2?family=Source+Sans+3:wght@400;600&display=swap',
  'playfair display, serif': 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600&display=swap',
  'manrope, sans-serif': 'https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700&display=swap',
  'raleway, sans-serif': 'https://fonts.googleapis.com/css2?family=Raleway:wght@400;500;600&display=swap',
  'dm sans, sans-serif': 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&display=swap',
  'work sans, sans-serif': 'https://fonts.googleapis.com/css2?family=Work+Sans:wght@400;500;600&display=swap',
  'karla, sans-serif': 'https://fonts.googleapis.com/css2?family=Karla:wght@400;600&display=swap',
  'nunito sans, sans-serif': 'https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@400;600&display=swap',
  'pt sans, sans-serif': 'https://fonts.googleapis.com/css2?family=PT+Sans:wght@400;700&display=swap',
  'oswald, sans-serif': 'https://fonts.googleapis.com/css2?family=Oswald:wght@400;500;600&display=swap',
  'space grotesk, sans-serif': 'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600&display=swap',
  'merriweather, serif': 'https://fonts.googleapis.com/css2?family=Merriweather:wght@400;700&display=swap',
  'libre baskerville, serif': 'https://fonts.googleapis.com/css2?family=Libre+Baskerville:wght@400;700&display=swap',
};

function normalizeFontKey(fontFamily = '') {
  return fontFamily
    .toLowerCase()
    .replace(/["']/g, '')
    .replace(/\s*,\s*/g, ', ')
    .replace(/\s+/g, ' ')
    .trim();
}

function ensureFontLoaded(fontFamily) {
  if (!fontFamily) return;
  const key = normalizeFontKey(fontFamily);
  const href = FONT_SOURCES[key];
  if (!href) return;
  if (document.querySelector(`link[data-ultimo-font="${key}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  link.setAttribute('data-ultimo-font', key);
  document.head.appendChild(link);
}

function generateSessionId() {
  const timestamp = new Date().getTime();
  const randomNum = Math.floor(Math.random() * 10000) + 1;
  return `${timestamp}-${randomNum}`;
}

// ── Colour helpers ─────────────────────────────────────────────────
// Used to derive the "Talk to a human" pill colours from the user's
// configured theme_color + header_font_color so the CTA matches the
// header instead of being a hardcoded faded purple.
function _hexToRgb(hex) {
  const m = String(hex || '').replace('#', '').trim();
  const v = m.length === 3 ? m.split('').map((c) => c + c).join('') : m.slice(0, 6);
  if (!/^[0-9a-fA-F]{6}$/.test(v)) return null;
  const n = parseInt(v, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}
function _rgbToHex({ r, g, b }) {
  const h = (v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}`;
}
function _relLum({ r, g, b }) {
  const f = (c) => {
    c /= 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}
function _contrast(a, b) {
  const la = _relLum(a);
  const lb = _relLum(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}
function _mix(a, b, t) {
  return { r: a.r + (b.r - a.r) * t, g: a.g + (b.g - a.g) * t, b: a.b + (b.b - a.b) * t };
}
function deriveLiveCtaColors(themeColorHex, headerFontColorHex) {
  const theme = _hexToRgb(themeColorHex) || { r: 86, g: 22, b: 234 };
  const white = { r: 255, g: 255, b: 255 };
  const black = { r: 0, g: 0, b: 0 };
  // The CTA must always read as part of the same brand surface as the
  // header — so the background is the configured theme colour and the
  // foreground is the configured header font colour, no WCAG override.
  const fg = _hexToRgb(headerFontColorHex) || white;
  return {
    bg: _rgbToHex(theme),
    fg: _rgbToHex(fg),
    hoverBg: _rgbToHex(_mix(theme, black, 0.12)),
    activeBg: _rgbToHex(_mix(theme, black, 0.2)),
    border: _rgbToHex(_mix(theme, black, 0.08)),
  };
}

// ── AI disclosure (EU AI Act Art. 50(1), applies from 2 August 2026) ──
// Visitors must be told they are talking to an AI at the latest at the first
// interaction. The strip under the header carries that notice. It is a system
// element: NOT part of widget_configuration and not customer-removable.
// 1:1 port of the widget's disclosure block (ultimo-bots-widget/src/index.js).
const AI_DISCLOSURE_TEXT = {
  en: 'You are chatting with an AI assistant',
  de: 'Sie chatten mit einem KI-Assistenten',
  fr: 'Vous discutez avec un assistant IA',
  es: 'Estás chateando con un asistente de IA',
  it: 'Stai chattando con un assistente IA',
  pt: 'Está a conversar com um assistente de IA',
  nl: 'Je chat met een AI-assistent',
  pl: 'Rozmawiasz z asystentem AI',
};

// Taken from the visitor's browser, not from the bot config — no settings
// column, no portal UI, and a French visitor on a German bot still reads it
// in their own language. Unknown locale falls back to English.
function resolveDisclosureText() {
  const lang = String(navigator.language || 'en').slice(0, 2).toLowerCase();
  return AI_DISCLOSURE_TEXT[lang] || AI_DISCLOSURE_TEXT.en;
}

// The icon carries the customer's theme colour, but a pale brand would be
// invisible on the light strip, so darken it (hue intact) until it clears the
// WCAG 3:1 floor for non-text UI. Same "never unreadable" idea as the CTA above.
function deriveDisclosureIconColor(themeColorHex) {
  const stripBg = { r: 247, g: 247, b: 249 };
  const black = { r: 0, g: 0, b: 0 };
  const theme = _hexToRgb(themeColorHex);
  if (!theme) return '#4a4658';
  let c = theme;
  for (let t = 0.1; t <= 0.6 && _contrast(c, stripBg) < 3; t += 0.1) {
    c = _mix(theme, black, t);
  }
  return _rgbToHex(c);
}

// Build agent avatar URL — supports `preset:person|headset|smile` keywords
// (1:1 from widget) which expand to inline-SVG data URIs.
function buildAgentAvatar(url, color) {
  if (!url) return null;
  const c = color || '#5616ea';
  const PRESET_SVGS = {
    'preset:person': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><circle cx="32" cy="32" r="32" fill="${c}"/><g transform="translate(14,12) scale(0.08)"><path fill="#fff" d="M224 256A128 128 0 1 0 224 0a128 128 0 1 0 0 256zm-45.7 48C79.8 304 0 383.8 0 482.3C0 498.7 13.3 512 29.7 512H418.3c16.4 0 29.7-13.3 29.7-29.7C448 383.8 368.2 304 269.7 304H178.3z"/></g></svg>`,
    'preset:headset': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><circle cx="32" cy="32" r="32" fill="${c}"/><g transform="translate(12,12) scale(0.078125)"><path fill="#fff" d="M256 48C141.1 48 48 141.1 48 256v40c0 13.3-10.7 24-24 24s-24-10.7-24-24V256C0 114.6 114.6 0 256 0S512 114.6 512 256V400.1c0 48.6-39.4 88-88.1 88L313.6 488c-8.3 14.3-23.8 24-41.6 24H240c-26.5 0-48-21.5-48-48s21.5-48 48-48h32c17.8 0 33.3 9.7 41.6 24l110.4 .1c22.1 0 40-17.9 40-40V256c0-114.9-93.1-208-208-208zM144 208h16c17.7 0 32 14.3 32 32V352c0 17.7-14.3 32-32 32H144c-35.3 0-64-28.7-64-64V272c0-35.3 28.7-64 64-64zm224 0c35.3 0 64 28.7 64 64v48c0 35.3-28.7 64-64 64H352c-17.7 0-32-14.3-32-32V240c0-17.7 14.3-32 32-32h16z"/></g></svg>`,
    'preset:smile': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><circle cx="32" cy="32" r="32" fill="${c}"/><g transform="translate(12,12) scale(0.078125)"><path fill="#fff" d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM164.1 325.5C182 346.2 212.6 368 256 368s74-21.8 91.9-42.5c5.8-6.7 15.9-7.4 22.6-1.6s7.4 15.9 1.6 22.6C349.8 372.1 311.1 400 256 400s-93.8-27.9-116.1-53.5c-5.8-6.7-5.1-16.8 1.6-22.6s16.8-5.1 22.6 1.6zM144.4 208a32 32 0 1 1 64 0 32 32 0 1 1 -64 0zm192-32a32 32 0 1 1 0 64 32 32 0 1 1 0-64z"/></g></svg>`,
  };
  if (PRESET_SVGS[url]) {
    return 'data:image/svg+xml,' + encodeURIComponent(PRESET_SVGS[url]);
  }
  return url;
}

const ERROR_MESSAGES = {
  rate_limit: 'Too many requests. Please wait a moment before trying again.',
  timeout: 'Sorry, the server took too long to respond. Please try again.',
  internal_error: 'Something went wrong. Please try again later.',
  validation_error: 'There was a problem with your request. Please try again.',
  bot_not_found: 'This chatbot is currently unavailable.',
  default: 'An unexpected error occurred. Please try again.'
};

// ── Agent activity timeline (SSE `status` events) ──────────────────────────
// Ported from the portal chat modal / kept behaviorally in sync with the
// widget (src/index.js status* functions). Local placeholder label shown the
// instant the visitor sends; the server's own "analyzing" event upserts it
// ~0.5s later, localized by the model's language signal. Browser language is
// the best first guess we have.
// Breathing room above the pinned user message (mirrors the widget's
// TOP_MARGIN; .chat-body has padding-top: 0, so this is the only air).
const TOP_MARGIN = 8;

const STATUS_ANALYZING_LABELS = {
  en: 'Analyzing your question…', de: 'Analysiere deine Frage…',
  fr: 'Analyse de votre question…', es: 'Analizando tu pregunta…',
  it: 'Analizza la tua domanda…', pt: 'Analisando sua pergunta…',
  nl: 'Analyseert je vraag…', pl: 'Analizuje twoje pytanie…',
};
function statusAnalyzingLabel() {
  const lang = (navigator.language || 'en').slice(0, 2).toLowerCase();
  return STATUS_ANALYZING_LABELS[lang] || STATUS_ANALYZING_LABELS.en;
}

// Typewriter for a status step's `detail` (the agent's composed search query).
// Top-level component (NOT inside Chat) so re-renders never remount it and
// restart the animation; it only re-types when the text itself changes.
const StatusDetail = ({ text }) => {
  const [shown, setShown] = useState(0);
  useEffect(() => {
    setShown(0);
    if (!text) return undefined;
    const iv = setInterval(() => {
      setShown((n) => (n >= text.length ? n : n + 2));
    }, 24);
    return () => clearInterval(iv);
  }, [text]);
  if (!text) return null;
  return (
    <span className="chat-status-detail">
      {'“'}{text.slice(0, shown)}{'”'}
    </span>
  );
};

const Chat = () => {
  const { botId } = useParams();
  const [config, setConfig] = useState(null);
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  // The visitor may dismiss the AI notice (by then they have been informed,
  // which is what Art. 50(1) asks for); the customer may not. In-memory only,
  // so it is back on the next page load rather than suppressed for good.
  const [aiNoticeDismissed, setAiNoticeDismissed] = useState(false);
  const [agentTyping, setAgentTyping] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  // Agent activity timeline (SSE `status` events). With the spacer-pin
  // scroll model the hand-off is 1:1 the portal chat modal's: steps build
  // up -> statusCollapsing (bubble fades as one piece, then folds while
  // invisible; answer text buffers) -> atomic swap -> reveal typing.
  // Ref mirror for the SSE closures.
  const [statusSteps, setStatusSteps] = useState([]);
  const [statusCollapsing, setStatusCollapsing] = useState(false);
  const statusCollapsingRef = useRef(false);
  const statusCollapseTimer = useRef(null);
  // Smooth character-reveal of the answer (see the portal chat modal): types
  // the buffered text from its first word and irons out bursty SSE chunks.
  const revealTimerRef = useRef(null);
  const revealActiveRef = useRef(false);
  const displayedLenRef = useRef(0);
  const answerDoneRef = useRef(false);
  // ── Spacer-pin scroll model + scroll-down arrow ──
  // 1:1 port of the portal chat modal's system (which mirrors the widget):
  // on send, the user message is pinned to the TOP of the viewport and an
  // invisible spacer below the messages absorbs the growing reply, so the
  // conversation never jumps. Once the reply outgrows the viewport the
  // spacer is exhausted and the scroll-down arrow appears — WITHOUT the
  // visitor having to scroll first. recalcSpacer never touches scrollTop.
  const [showScrollDown, setShowScrollDown] = useState(false);
  const bottomSpacerRef = useRef(null);
  const spacerActiveRef = useRef(false);   // spacer currently pinning the user msg
  const programmaticScrollRef = useRef(false); // our smooth scroll is in flight
  const pendingUserScroll = useRef(false); // set on send; consumed by positioning
  // Widget-1:1 scroll state (src/index.js): who owns the scroll right now.
  const userScrolledAwayRef = useRef(false);   // visitor scrolled up during streaming
  const ignoreScrollEventsRef = useRef(false); // our own scrollTop writes
  const smoothScrollTargetRef = useRef(null);  // exact smooth-scroll completion check
  const isStreamingRef = useRef(false);        // widget's isStreamingState
  const liveActiveRef = useRef(false);         // mirror for callbacks defined pre-hook
  const didInitialScrollRef = useRef(false);   // one-shot restore-to-bottom on mount
  useEffect(() => () => {
    if (statusCollapseTimer.current) clearTimeout(statusCollapseTimer.current);
    if (revealTimerRef.current) clearInterval(revealTimerRef.current);
  }, []);
  const messagesEndRef = useRef(null);
  const currentBotMessage = useRef('');
  const currentBotProducts = useRef(null); // product cards for the in-flight reply (SSE `products` event)
  const menuRef = useRef(null);
  const markedRef = useRef(null);
  const chatBodyRef = useRef(null);
  const messagesRef = useRef([]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  // marked is bundled (no runtime CDN import) — available immediately.
  useEffect(() => {
    markedRef.current = marked;
  }, []);

  // Initialize session (conversationStore: shared by the tabs, see there)
  const resumeOnlyRef = useRef(false);
  const [conversationReady, setConversationReady] = useState(false);
  const decidedRef = useRef(false);
  const liveRef = useRef(null);
  const sessionIdRef = useRef('');
  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);
  const isBusyRef = useRef(false);
  useEffect(() => { isBusyRef.current = isBusy || loading; }, [isBusy, loading]);
  useEffect(() => {
    if (!botId) return;
    resumeOnlyRef.current = startConversationStorage(botId).resumeOnly;
    const k = convKeys(botId);
    const sid = convGet(k.session) || generateSessionId();
    setSessionId(sid);
    convSet(k.session, sid);
  }, [botId]);

  // Fetch widget configuration
  useEffect(() => {
    const fetchConfig = async () => {
      if (!botId) return;
      try {
        // Data minimisation: origin + path only, never query or fragment.
        const hostPageUrl = encodeURIComponent(window.location.origin + window.location.pathname);
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}widget_configuration/${botId}?host_url=${hostPageUrl}`,
          { cache: 'no-store' }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setConfig(data);
        const fontFamily = (data.font_family && String(data.font_family).trim()) || '"DM Sans", sans-serif';
        ensureFontLoaded(fontFamily);
        document.documentElement.style.setProperty('--chat-font-family', fontFamily);
      } catch (err) {
        console.error('Widget config load failed:', err);
      }
    };
    fetchConfig();
  }, [botId]);

  // â”€â”€ Chat history persistence â”€â”€
  // Persist all "rendered" messages (sender bubbles + system notices)
  // so that on reload we restore the full transcript including agent replies.
  const saveChatHistory = useCallback(() => {
    if (!botId) return;
    try {
      const persistable = (messagesRef.current || []).filter((m) => !m.isStreaming);
      // Don't overwrite saved history with an empty array — that would
      // happen on the very first render (before the welcome effect runs)
      // and cause the welcome bubbles to be lost on the next reload.
      if (persistable.length === 0) return;
      convSet(convKeys(botId).history, JSON.stringify(persistable));
      convTouch(botId);
    } catch { /* ignore */ }
  }, [botId]);

  // The saved transcript, or null. Only a non-empty one counts: an empty array
  // can sneak in if the persistence effect runs once with the initial empty
  // `messages` state before the welcome bubbles are rendered.
  const storedTranscript = useCallback(() => {
    const saved = convGet(convKeys(botId).history);
    if (!saved) return null;
    try {
      const parsed = JSON.parse(saved);
      return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
    } catch {
      convRemove(convKeys(botId).history);
      return null;
    }
  }, [botId]);

  const welcomeBubbles = useCallback(() => {
    const welcomeMessages = Array.isArray(config?.welcome_message)
      ? config.welcome_message
      : config?.welcome_message
      ? [config.welcome_message]
      : ['Welcome! How can I help you today?'];
    return welcomeMessages.map((text) => ({ text, sender: 'bot', isHtml: false }));
  }, [config]);

  // Welcome / restore, with the replies the team wrote while the visitor was
  // away. After a break of more than 30 minutes the old conversation only
  // continues if there are such replies; otherwise this visit starts a new one.
  useEffect(() => {
    if (!config || !sessionId || decidedRef.current) return undefined;
    let cancelled = false;
    (async () => {
      const live = liveRef.current;
      const replies = live ? await live.fetchFollowUps() : [];
      if (cancelled || decidedRef.current) return;
      decidedRef.current = true;
      const resumeOnly = resumeOnlyRef.current;
      resumeOnlyRef.current = false;
      const k = convKeys(botId);
      if (resumeOnly && !replies.length) {
        [k.history, k.token, k.agentMsg, k.resume].forEach(convRemove);
        const fresh = generateSessionId();
        convSet(k.session, fresh);
        setSessionId(fresh);
        setMessages(welcomeBubbles());
        setConversationReady(true);
        return;
      }
      const restored = storedTranscript() || welcomeBubbles();
      setMessages([...restored, ...replies.map((m) => ({ text: m.content, sender: 'agent', isHtml: true }))]);
      setConversationReady(true);
      if (replies.length && live) {
        live.ackFollowUps(replies.map((m) => m.id));
        if (resumeOnly) live.ensureHeartbeatStarted();
      }
    })();
    return () => { cancelled = true; };
  }, [config, botId, sessionId, storedTranscript, welcomeBubbles]);

  // ── Widget-1:1 scroll system (ported function by function from
  // ultimo-bots-widget/src/index.js). Any change here must be cross-applied
  // to the widget, same PR. ──

  // Recalculate spacer so the last user message stays pinned at the top.
  // Key insight (widget): lastUserMsg.offsetTop is CONSTANT during streaming,
  // so the anchor target is always msgOffsetTop - TOP_MARGIN and the anchor
  // write is a true no-op when already correct — no jitter.
  const recalcSpacer = useCallback(() => {
    const container = chatBodyRef.current;
    const spacer = bottomSpacerRef.current;
    if (!container || !spacer) return 0;
    // In live agent mode, spacer is always disabled — scroll-to-bottom only.
    if (liveActiveRef.current) {
      spacer.style.height = '0px';
      spacerActiveRef.current = false;
      return 0;
    }
    const allUserMsgs = container.querySelectorAll('.chat-message-row.user');
    const lastUserMsg = allUserMsgs[allUserMsgs.length - 1];
    if (!lastUserMsg) {
      spacer.style.height = '0px';
      spacerActiveRef.current = false;
      return 0;
    }
    const containerH = container.clientHeight;
    const msgOffsetTop = lastUserMsg.offsetTop;
    const naturalContentH = spacer.offsetTop;
    const contentBelowUserMsg = naturalContentH - msgOffsetTop;
    // TOP_MARGIN = breathing room above the pinned message; the body's bottom
    // padding is subtracted so the pinned position IS the absolute scroll
    // bottom (no leftover scrollbar travel after the arrow).
    const padBottom = parseFloat(getComputedStyle(container).paddingBottom) || 0;
    const neededSpacer = Math.max(0, containerH - contentBelowUserMsg - TOP_MARGIN - padBottom);
    spacer.style.height = neededSpacer + 'px';
    spacerActiveRef.current = neededSpacer > 0;
    // Anchor scroll so the user msg stays at TOP_MARGIN from the viewport top
    // (widget behaviour). Skip while the visitor scrolled away or our smooth
    // scroll is animating.
    if (spacerActiveRef.current && !userScrolledAwayRef.current && !programmaticScrollRef.current) {
      const target = msgOffsetTop - TOP_MARGIN;
      if (Math.abs(container.scrollTop - target) > 1) {
        ignoreScrollEventsRef.current = true;
        container.scrollTop = target;
        requestAnimationFrame(() => { ignoreScrollEventsRef.current = false; });
      }
    }
    return neededSpacer;
  }, []);

  // Position the just-sent user message at the top (widget doPositioning).
  const doPositioning = useCallback(() => {
    const container = chatBodyRef.current;
    const spacer = bottomSpacerRef.current;
    if (!container || !spacer || !pendingUserScroll.current) return;
    // In live agent mode, just scroll to bottom — no spacer pinning.
    if (liveActiveRef.current) {
      pendingUserScroll.current = false;
      container.scrollTop = container.scrollHeight;
      return;
    }
    pendingUserScroll.current = false;
    // Collapsing the spacer to re-measure clamps scrollTop upward when the
    // raw content is shorter than the viewport — restore it after sizing so
    // the smooth scroll starts from where the visitor actually was.
    const prevScrollTop = container.scrollTop;
    spacer.style.height = '0px';
    spacerActiveRef.current = false;
    // Set programmaticScroll FIRST so recalcSpacer skips its instant anchor —
    // we want a smooth scroll instead.
    programmaticScrollRef.current = true;
    recalcSpacer();
    container.scrollTop = prevScrollTop;
    if (spacerActiveRef.current) {
      const allUserMsgs = container.querySelectorAll('.chat-message-row.user');
      const lastUserMsg = allUserMsgs[allUserMsgs.length - 1];
      smoothScrollTargetRef.current = lastUserMsg ? lastUserMsg.offsetTop - TOP_MARGIN : 0;
      container.scrollTo({ top: smoothScrollTargetRef.current, behavior: 'smooth' });
    } else {
      programmaticScrollRef.current = false;
    }
  }, [recalcSpacer]);

  // Arrow visibility (widget 1:1): hidden while our smooth scroll animates
  // and during the timeline fade-out; otherwise shown whenever the bottom is
  // out of view — INCLUDING while the reply streams.
  const updateScrollDownVisibility = useCallback(() => {
    const container = chatBodyRef.current;
    if (!container) return;
    if (programmaticScrollRef.current || statusCollapsingRef.current) {
      setShowScrollDown(false);
      return;
    }
    const { scrollTop, scrollHeight, clientHeight } = container;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 40;
    setShowScrollDown(!isAtBottom && scrollHeight > clientHeight);
  }, []);

  // Scroll handler (widget 1:1): ignore our own scrollTop writes; detect
  // smooth-scroll completion by exact target OR bottom.
  const handleBodyScroll = useCallback(() => {
    if (ignoreScrollEventsRef.current) return;
    const c = chatBodyRef.current;
    if (!c) return;
    if (programmaticScrollRef.current) {
      const t = smoothScrollTargetRef.current;
      const atTarget = t !== null && Math.abs(c.scrollTop - t) < 2;
      const atBottom = c.scrollHeight - c.scrollTop - c.clientHeight < 2;
      if (atTarget || atBottom) {
        programmaticScrollRef.current = false;
        smoothScrollTargetRef.current = null;
      }
      return;
    }
    updateScrollDownVisibility();
  }, [updateScrollDownVisibility]);

  // Arrow click (widget 1:1): re-pin the last user message when the spacer
  // is active (short reply -> the user message returns to the top), else
  // absolute bottom; smooth either way.
  const onScrollDownClick = useCallback(() => {
    const c = chatBodyRef.current;
    if (!c) return;
    if (liveActiveRef.current) {
      c.scrollTop = c.scrollHeight;
      setShowScrollDown(false);
      return;
    }
    userScrolledAwayRef.current = false;
    programmaticScrollRef.current = true;
    recalcSpacer();
    const allUserMsgs = c.querySelectorAll('.chat-message-row.user');
    const lastUserMsg = allUserMsgs[allUserMsgs.length - 1];
    if (spacerActiveRef.current && lastUserMsg) {
      smoothScrollTargetRef.current = lastUserMsg.offsetTop - TOP_MARGIN;
    } else {
      smoothScrollTargetRef.current = c.scrollHeight - c.clientHeight;
    }
    c.scrollTo({ top: smoothScrollTargetRef.current, behavior: 'smooth' });
    setShowScrollDown(false);
  }, [recalcSpacer]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    if (menuOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  // Close menu on Escape
  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape' && menuOpen) setMenuOpen(false); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [menuOpen]);

  const parseMarkdown = (text) => {
    const raw = markedRef.current
      ? markedRef.current.parse(String(text ?? ''))
      : escapeHtml(text).replace(/\n/g, '<br>');
    return sanitizeHtml(raw);
  };

  // â”€â”€ Hook callbacks (rendered as messages list mutations) â”€â”€
  const appendMessageCb = useCallback((text, sender) => {
    setMessages((prev) => {
      const updated = [...prev, { text, sender, isHtml: sender === 'agent' || sender === 'bot' }];
      return updated;
    });
  }, []);

  const appendSystemNoticeCb = useCallback((text, opts = {}) => {
    setMessages((prev) => {
      // Dedup: skip if last system notice has same dedupKey or text
      const last = prev[prev.length - 1];
      if (last && last.kind === 'system') {
        // Only the part before ':' counts: transcripts saved earlier carry 'joined:<name>'.
        if (opts.dedupKey && String(last.dedupKey).split(':')[0] === opts.dedupKey) return prev;
        if (!opts.dedupKey && last.text === text) return prev;
      }
      // Idempotent waiting notice â€” don't duplicate if one already exists anywhere
      if (opts.waiting && prev.some((m) => m.kind === 'system' && m.waiting)) return prev;
      return [...prev, {
        kind: 'system',
        text,
        waiting: !!opts.waiting,
        dedupKey: opts.dedupKey || null,
      }];
    });
  }, []);

  const removeWaitingNoticeCb = useCallback(() => {
    setMessages((prev) => prev.filter((m) => !(m.kind === 'system' && m.waiting)));
  }, []);

  const markVisitorDeliveredCb = useCallback((count) => {
    if (!count || count < 1) count = 1;
    setMessages((prev) => {
      // Find the last `count` user messages without delivery, mark delivered
      const updated = [...prev];
      let applied = 0;
      // Walk from oldest to newest applying to messages without status
      for (let i = 0; i < updated.length && applied < count; i++) {
        const m = updated[i];
        if (m.sender === 'user' && !m.deliveryStatus) {
          updated[i] = { ...m, deliveryStatus: 'delivered' };
          applied += 1;
        }
      }
      return updated;
    });
  }, []);

  const markVisitorReadCb = useCallback(() => {
    setMessages((prev) => prev.map((m) => {
      if (m.sender !== 'user') return m;
      // Idempotent: never downgrade
      if (m.deliveryStatus === 'read') return m;
      return { ...m, deliveryStatus: 'read' };
    }));
  }, []);

  const setAgentTypingCb = useCallback((b) => setAgentTyping(!!b), []);

  const injectWelcomeIfEmptyCb = useCallback(() => {
    setMessages((prev) => {
      if (prev.length > 0) return prev;
      const welcomeMessages = Array.isArray(config?.welcome_message)
        ? config.welcome_message
        : config?.welcome_message
        ? [config.welcome_message]
        : [];
      if (!welcomeMessages.length) return prev;
      return welcomeMessages.map((text) => ({ text, sender: 'bot', isHtml: false }));
    });
  }, [config]);

  // â”€â”€ Live agent hook â”€â”€
  const live = useLiveAgent({
    botId,
    sessionId,
    setSessionId,
    appendMessage: appendMessageCb,
    appendSystemNotice: appendSystemNoticeCb,
    removeWaitingNotice: removeWaitingNoticeCb,
    markVisitorDelivered: markVisitorDeliveredCb,
    markVisitorRead: markVisitorReadCb,
    setAgentTyping: setAgentTypingCb,
    injectWelcomeIfEmpty: injectWelcomeIfEmptyCb,
    saveChatHistory,
  });

  liveRef.current = live;

  // ── Voice input (useVoiceInput.js): the transcription lands in the input field, after what is already there ──
  const chatInputRef = useRef(null);
  const voice = useVoiceInput({
    botId,
    enabled: config?.voice_input === true,
    onText: (text) => {
      setUserInput((prev) => (prev.trim() ? `${prev.trimEnd()} ${text}` : text));
      live.notifyTyping(true);
      requestAnimationFrame(() => {
        const el = chatInputRef.current;
        if (el) { el.focus(); el.setSelectionRange(el.value.length, el.value.length); }
      });
    },
  });

  // Persist history whenever messages change (debounced via microtask) — not
  // before the restore above has decided, or welcome bubbles would overwrite
  // a conversation that is about to continue.
  useEffect(() => {
    if (conversationReady) saveChatHistory();
  }, [messages, saveChatHistory, conversationReady]);

  // Other tabs share the conversation: follow a new one they start (clear chat,
  // token reset) and show messages they add, once this tab is not mid-reply.
  useEffect(() => {
    if (!botId || !convShared()) return undefined;
    const k = convKeys(botId);
    let retry = null;
    const sync = () => {
      const live = liveRef.current;
      const storedSid = convGet(k.session);
      if (storedSid && storedSid !== sessionIdRef.current) {
        live && live.followConversation();
        setSessionId(storedSid);
        const transcript = storedTranscript();
        setMessages(transcript || welcomeBubbles());
        if (transcript && transcript.some((m) => m && m.sender === 'user') && live) live.ensureHeartbeatStarted();
        return;
      }
      if (isBusyRef.current) {
        if (!retry) retry = setTimeout(() => { retry = null; sync(); }, 1500);
        return;
      }
      const transcript = storedTranscript();
      if (transcript) {
        setMessages(transcript);
        if (transcript.some((m) => m && m.sender === 'user') && live) live.ensureHeartbeatStarted();
      }
    };
    const onStorage = (event) => {
      if (event.key === k.session || event.key === k.history) sync();
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
      if (retry) clearTimeout(retry);
    };
  }, [botId, storedTranscript, welcomeBubbles]);

  const liveActive = live.liveSessionStatus === 'agent_requested' || live.liveSessionStatus === 'agent_joined';

  // ── Widget-1:1 scroll effects ──
  // liveActive mirror for the pre-hook callbacks; entering live mode zeroes
  // the spacer and scrolls to the bottom (widget live branch).
  useLayoutEffect(() => {
    liveActiveRef.current = liveActive;
    if (liveActive) {
      if (bottomSpacerRef.current) {
        bottomSpacerRef.current.style.height = '0px';
        spacerActiveRef.current = false;
      }
      const el = chatBodyRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }
  }, [liveActive]);

  // One-shot restore-to-bottom on mount (the widget scrolls hard to the
  // bottom after replaying the stored history).
  useLayoutEffect(() => {
    if (didInitialScrollRef.current) return;
    if (!config || messages.length === 0) return;
    didInitialScrollRef.current = true;
    const el = chatBodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [config, messages]);

  // After every message change (widget appendMessage/updateStreamingBotMessage
  // tail): consume a pending send positioning, otherwise absorb growth into
  // the spacer (recalc anchors the pin internally) and refresh the arrow.
  // In live mode: classic follow-to-bottom (widget handleIncomingLiveContent).
  useLayoutEffect(() => {
    const el = chatBodyRef.current;
    if (!el) return;
    if (liveActive) {
      el.scrollTop = el.scrollHeight;
      updateScrollDownVisibility();
      return;
    }
    if (pendingUserScroll.current) {
      doPositioning();
      updateScrollDownVisibility();
      return;
    }
    if (spacerActiveRef.current && !programmaticScrollRef.current && !userScrolledAwayRef.current) {
      recalcSpacer();
    }
    updateScrollDownVisibility();
  }, [messages, agentTyping, liveActive, doPositioning, recalcSpacer, updateScrollDownVisibility]);

  // Status timeline growth (widget statusUpsertStep tail): absorb each row
  // into the spacer in the same layout pass — also while the send smooth
  // scroll is still animating (recalc skips its anchor then and only resizes,
  // keeping scrollHeight constant so the animation target stays valid).
  useLayoutEffect(() => {
    if (statusCollapsing) return; // collapse pre-grew the spacer and owns it
    if (spacerActiveRef.current) recalcSpacer();
    updateScrollDownVisibility();
  }, [statusSteps, statusCollapsing, loading, recalcSpacer, updateScrollDownVisibility]);

  // Visitor scroll-away detection during streaming (widget 1:1): touch on
  // mobile, wheel/mousedown on desktop — reliable where scroll events from
  // programmatic writes are indistinguishable from user scrolls.
  useEffect(() => {
    const el = chatBodyRef.current;
    if (!el) return undefined;
    const markAway = () => {
      if (isStreamingRef.current && !userScrolledAwayRef.current) {
        userScrolledAwayRef.current = true;
        updateScrollDownVisibility();
      }
    };
    if (window.matchMedia('(max-width: 768px)').matches) {
      let userTouching = false;
      const onTouchStart = () => { userTouching = true; };
      const onTouchEnd = () => { setTimeout(() => { userTouching = false; }, 100); };
      const onScroll = () => { if (userTouching) markAway(); };
      el.addEventListener('touchstart', onTouchStart, { passive: true });
      el.addEventListener('touchend', onTouchEnd, { passive: true });
      el.addEventListener('scroll', onScroll, { passive: true });
      return () => {
        el.removeEventListener('touchstart', onTouchStart);
        el.removeEventListener('touchend', onTouchEnd);
        el.removeEventListener('scroll', onScroll);
      };
    }
    el.addEventListener('wheel', markAway, { passive: true });
    el.addEventListener('mousedown', markAway);
    return () => {
      el.removeEventListener('wheel', markAway);
      el.removeEventListener('mousedown', markAway);
    };
  }, [config, updateScrollDownVisibility]);

  // Container resizes (mobile keyboard dismiss changes clientHeight): re-size
  // the spacer so the user message stays pinned (widget ResizeObserver).
  useEffect(() => {
    const el = chatBodyRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    let lastH = el.clientHeight;
    const ro = new ResizeObserver(() => {
      const h = el.clientHeight;
      if (h === lastH) return;
      lastH = h;
      if (spacerActiveRef.current) recalcSpacer();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [config, recalcSpacer]);

  // â”€â”€ Talk-to-a-human button label & handler â”€â”€
  const liveButtonLabel = (() => {
    if (live.liveSessionStatus === 'agent_joined') return 'Disconnect Agent';
    if (live.liveSessionStatus === 'agent_requested') return 'Waiting for agent…';
    return live.liveSettings.request_button_text || 'Talk to a human';
  })();
  const liveButtonDisabled = live.liveSessionStatus === 'agent_requested';
  const liveCtaVisible = live.liveSettings.show_request_button && live.agentAvailable;
  const onLiveButtonClick = useCallback(() => {
    setMenuOpen(false);
    if (live.liveSessionStatus === 'agent_joined') {
      setShowDisconnectConfirm(true);
      return;
    }
    if (live.liveSessionStatus === 'agent_requested') return;
    live.requestAgent();
  }, [live]);

  // â”€â”€ Clear chat â”€â”€
  const performClear = useCallback(() => {
    setMenuOpen(false);
    live.resetLiveSession();
    // Old history first, new session id last: other tabs react to the id and
    // must not find the old history under it.
    convRemove(convKeys(botId).history);
    const newSessionId = generateSessionId();
    setSessionId(newSessionId);
    convSet(convKeys(botId).session, newSessionId);
    const welcomeMessages = Array.isArray(config?.welcome_message)
      ? config.welcome_message
      : config?.welcome_message
      ? [config.welcome_message]
      : ['Welcome! How can I help you today?'];
    setMessages(welcomeMessages.map((text) => ({ text, sender: 'bot', isHtml: false })));
    setAgentTyping(false);
    currentBotMessage.current = '';
    // A cleared chat has nothing to scroll to — retire the arrow and every
    // piece of pin-scroll state so the fresh conversation starts clean.
    setShowScrollDown(false);
    pendingUserScroll.current = false;
    programmaticScrollRef.current = false;
    smoothScrollTargetRef.current = null;
    userScrolledAwayRef.current = false;
    ignoreScrollEventsRef.current = false;
    isStreamingRef.current = false;
    spacerActiveRef.current = false;
    if (bottomSpacerRef.current) bottomSpacerRef.current.style.height = '0px';
    if (statusCollapseTimer.current) clearTimeout(statusCollapseTimer.current);
    statusCollapsingRef.current = false;
    setStatusCollapsing(false);
    setStatusSteps([]);
  }, [botId, config, live]);

  const clearChat = useCallback(() => {
    setMenuOpen(false);
    if (liveActive) {
      setShowClearConfirm(true);
      return;
    }
    performClear();
  }, [liveActive, performClear]);

  // â”€â”€ Cancel waiting-for-agent â”€â”€
  const onCancelWaiting = useCallback(() => {
    live.cancelRequest();
  }, [live]);

  // â”€â”€ Disconnect agent confirm â”€â”€
  const onConfirmDisconnect = useCallback(() => {
    setShowDisconnectConfirm(false);
    live.disconnectAgent();
  }, [live]);

  // â”€â”€ Send message â”€â”€
  const sendMessage = async (messageOverride) => {
    if (isBusy) return;
    const text = typeof messageOverride === 'string' ? messageOverride : userInput.trim();
    if (!text || !botId || !sessionId) return;

    // Track first message â†’ start heartbeat (for portal "new visitor" notif)
    live.ensureHeartbeatStarted();

    // Optimistically add user message. In AI mode this send pins the user
    // message to the top (widget: pendingUserScroll/isStreamingState/
    // userScrolledAway are set on send; doPositioning consumes the flag in
    // the same commit's layout effect).
    if (!liveActive) {
      pendingUserScroll.current = true;
      isStreamingRef.current = true;
      userScrolledAwayRef.current = false;
    }
    const userMsg = { text, sender: 'user', isHtml: false };
    setMessages((prev) => [...prev, userMsg]);
    if (typeof messageOverride !== 'string') setUserInput('');

    // Live mode: fire-and-forget, no AI streaming
    if (liveActive) {
      live.sendVisitorMessageLive(text);
      return;
    }

    setIsBusy(true);
    setLoading(true);
    currentBotMessage.current = '';
    currentBotProducts.current = null;
    // Fresh reply -> fresh activity timeline + reveal state.
    if (statusCollapseTimer.current) clearTimeout(statusCollapseTimer.current);
    if (revealTimerRef.current) clearInterval(revealTimerRef.current);
    revealTimerRef.current = null;
    revealActiveRef.current = false;
    displayedLenRef.current = 0;
    answerDoneRef.current = false;
    statusCollapsingRef.current = false;
    setStatusCollapsing(false);
    setStatusSteps([]);
    setShowScrollDown(false);

    const url = `${process.env.REACT_APP_API_BASE_URL}chatbot_response?user_input=${encodeURIComponent(text)}&session_id=${sessionId}&bot_id=${botId}&language=english&platform=chat_link`;

    // Render exactly `answerText` as the (single) in-flight bot message. All
    // paths go through here — the reveal loop with a slice, finalization with
    // the full buffer — so there is one writer and no competing states.
    const renderBotText = (answerText) => {
      setMessages((msgs) => {
        const last = msgs[msgs.length - 1];
        const products = currentBotProducts.current || (last?.sender === 'bot' && last?.isStreaming ? last.products : null);
        if (last?.sender === 'bot' && last?.isStreaming) {
          return [...msgs.slice(0, -1), { ...last, text: answerText, products }];
        }
        return [...msgs, { text: answerText, sender: 'bot', isHtml: true, isStreaming: true, products }];
      });
    };

    const stopReveal = () => {
      if (revealTimerRef.current) {
        clearInterval(revealTimerRef.current);
        revealTimerRef.current = null;
      }
      revealActiveRef.current = false;
    };

    const clearStatusTimeline = () => {
      if (statusCollapseTimer.current) clearTimeout(statusCollapseTimer.current);
      statusCollapsingRef.current = false;
      setStatusCollapsing(false);
      setStatusSteps([]);
    };

    const finish = () => {
      setLoading(false);
      setIsBusy(false);
      stopReveal();
      clearStatusTimeline();
      isStreamingRef.current = false;
      setMessages((msgs) => msgs.map((m) => ({ ...m, isStreaming: false })));
      // Widget finish tail: re-settle the spacer and the arrow — and NOTHING
      // scrolls anywhere. The pin holds; the visitor reads from the top.
      recalcSpacer();
      updateScrollDownVisibility();
    };

    return new Promise((resolve) => {
      const es = new EventSource(url);
      let firstChunk = true;
      let hasError = false;
      let finalized = false;

      const finalizeReply = () => {
        if (finalized) return;
        finalized = true;
        stopReveal();
        renderBotText(currentBotMessage.current);
        finish();
        resolve();
      };

      // Character-level reveal: every 30ms show a few more characters of the
      // buffer (adaptive backlog catch-up), so the answer types smoothly from
      // its FIRST word and bursty SSE chunks are ironed out.
      const startReveal = () => {
        if (revealTimerRef.current) clearInterval(revealTimerRef.current);
        revealActiveRef.current = true;
        revealTimerRef.current = setInterval(() => {
          const full = currentBotMessage.current;
          if (displayedLenRef.current < full.length) {
            const backlog = full.length - displayedLenRef.current;
            displayedLenRef.current = Math.min(
              full.length,
              displayedLenRef.current + Math.max(1, Math.ceil(backlog / 25))
            );
            renderBotText(full.slice(0, displayedLenRef.current));
          } else if (answerDoneRef.current) {
            finalizeReply();
          }
        }, 30);
      };

      // The answer is starting. Choreography — 1:1 the portal chat modal's
      // (the spacer-pin model makes it identical): (1) kill leftover
      // positioning attempts + the smooth-scroll flag (their re-measure on
      // setLoading(false) was the up-then-down whole-chat jump; a stale flag
      // suppresses the arrow), (2) pre-grow the spacer by the timeline
      // block's exact height so the fading bubble can never SHRINK
      // scrollHeight (shrinking clamps scrollTop -> trembling), (3) bubble
      // fades as ONE piece then folds while invisible — the avatar sits
      // outside and never moves, (4) atomic swap: steps out + first
      // characters in, one commit, then the reveal loop types the rest.
      const collapseStatus = () => {
        // Widget firstChunk snap: if the send smooth-scroll is still
        // animating, snap to the pin BEFORE any DOM mutations so we own the
        // scrollTop value (a mid-animation clamp would shift the view). If
        // the visitor scrolled away, just cancel the animation.
        const container = chatBodyRef.current;
        if (programmaticScrollRef.current && !userScrolledAwayRef.current && container) {
          programmaticScrollRef.current = false;
          smoothScrollTargetRef.current = null;
          const allUserMsgs = container.querySelectorAll('.chat-message-row.user');
          const lastUserMsg = allUserMsgs[allUserMsgs.length - 1];
          if (lastUserMsg) {
            ignoreScrollEventsRef.current = true;
            container.scrollTop = lastUserMsg.offsetTop - TOP_MARGIN;
            requestAnimationFrame(() => { ignoreScrollEventsRef.current = false; });
          }
        } else if (programmaticScrollRef.current) {
          programmaticScrollRef.current = false;
          smoothScrollTargetRef.current = null;
        }
        const spacer = bottomSpacerRef.current;
        const block = container ? container.querySelector('.chat-status-message') : null;
        if (block && spacer) {
          const current = parseFloat(spacer.style.height || '0') || 0;
          spacer.style.height = `${current + block.offsetHeight}px`;
          spacerActiveRef.current = true;
        }
        statusCollapsingRef.current = true;
        setStatusCollapsing(true);
        if (statusCollapseTimer.current) clearTimeout(statusCollapseTimer.current);
        statusCollapseTimer.current = setTimeout(() => {
          statusCollapsingRef.current = false;
          setStatusCollapsing(false);
          setStatusSteps([]);
          displayedLenRef.current = Math.min(2, currentBotMessage.current.length);
          renderBotText(currentBotMessage.current.slice(0, displayedLenRef.current));
          startReveal();
        }, 460);
      };

      es.onmessage = ({ data: chunk }) => {
        if (chunk === 'end of response') return;
        if (firstChunk) {
          setLoading(false);
          firstChunk = false;
          collapseStatus();
        }
        currentBotMessage.current += chunk.replace(/<newline>/g, '\n');
        // While the timeline fades and while the reveal loop is typing, the
        // buffer is the single source of truth — no direct rendering here.
      };

      // Agent activity timeline: server-localized steps for what the agent is
      // doing right now ({key, label, phase, detail?, transient?}). Every step
      // PERSISTS (renderer checks earlier rows off in place); a mid-list
      // transient filler is never re-activated; a new step replaces a
      // TRAILING transient ("Analyzing…" -> first real activity).
      es.addEventListener('status', ({ data }) => {
        if (!firstChunk) return;
        try {
          const step = JSON.parse(data)?.status;
          if (step && step.key && step.label) {
            setStatusSteps((prev) => {
              const idx = prev.findIndex((s) => s.key === step.key);
              if (idx >= 0) {
                if (step.transient && idx < prev.length - 1) return prev;
                const next = [...prev];
                next[idx] = { ...next[idx], ...step };
                return next;
              }
              // EVERY step persists — including the "Analyzing…" /
              // "Processing…" fillers: the timeline only grows downward, and
              // a finished step keeps its place and gets the green check.
              return [...prev, step];
            });
          }
        } catch (err) {
          console.warn('Could not parse status event:', err);
        }
      });

      // Structured product cards ride a dedicated SSE event so the text stream
      // stays pure markdown. They normally arrive after the answer text; the
      // reveal loop (or finalization) renders them from the ref.
      es.addEventListener('products', ({ data }) => {
        try {
          const parsed = JSON.parse(data);
          if (Array.isArray(parsed.products) && parsed.products.length > 0) {
            currentBotProducts.current = parsed.products;
            if (!statusCollapsingRef.current && !revealActiveRef.current) {
              renderBotText(currentBotMessage.current);
            }
          }
        } catch (err) {
          console.warn('Could not parse products event:', err);
        }
      });

      es.addEventListener('end', () => {
        es.close();
        if (hasError) return; // the error path already finalized
        answerDoneRef.current = true;
        // A running fade-out/reveal finishes typing the buffer first
        // (finalizeReply fires from the reveal tick once caught up);
        // finalize immediately only when nothing is animating.
        if (!statusCollapsingRef.current && !revealActiveRef.current) {
          finalizeReply();
        }
      });

      es.addEventListener('error', (e) => {
        hasError = true;
        es.close();
        let errorType = 'default';
        try {
          if (e?.data) {
            if (e.data === 'Timeout while generating response') errorType = 'timeout';
            else if (e.data === 'Internal server error') errorType = 'internal_error';
            else {
              const errorData = JSON.parse(e.data);
              errorType = errorData.type || 'default';
            }
          }
        } catch {
          console.warn('Could not parse error data:', e?.data);
        }
        const errorMessage = ERROR_MESSAGES[errorType] || ERROR_MESSAGES.default;
        stopReveal();
        // Show whatever partial answer was buffered before the error, then
        // the error bubble under it.
        if (currentBotMessage.current) renderBotText(currentBotMessage.current);
        setMessages((prev) => [...prev, { text: errorMessage, sender: 'bot', isHtml: false }]);
        finish();
        resolve();
      });

      es.onerror = () => {
        if (hasError) return;
        if (es.readyState === EventSource.CLOSED || es.readyState === EventSource.CONNECTING) {
          hasError = true;
          es.close();
          stopReveal();
          if (currentBotMessage.current) renderBotText(currentBotMessage.current);
          setMessages((prev) => [...prev, { text: 'Connection lost. Please try again.', sender: 'bot', isHtml: false }]);
          finish();
          resolve();
        }
      };
    });
  };

  // Visitor typing notifications (only meaningful when an agent is connected)
  const onInputChange = (e) => {
    const v = e.target.value;
    setUserInput(v);
    live.notifyTyping(v.trim().length > 0);
  };

  if (!config) {
    return (
      <div className="chat-fullscreen-root">
        <div className="chat-loading-screen">
          <div className="chat-loading-dots"><div></div><div></div><div></div></div>
        </div>
      </div>
    );
  }

  const themeColor = config.theme_color || '#0082ba';
  const hoverColor = config.button_hover_color || '#0595d3';
  const headerFontColor = config.header_font_color || '#ffffff';
  const headerText = config.header_text || 'Chat with us!';
  const fontFamily = (config.font_family && String(config.font_family).trim()) || '"DM Sans", sans-serif';
  const headerIcon = config.header_icon_path || null;
  const avatar = config.avatar_icon_path || null;
  const inputPlaceholder = config.input_placeholder || 'Type your message...';
  const removePoweredBy = config.remove_powered_by ?? false;

  // Derive "Talk to a human" CTA colours from the user's theme + header
  // font so the button reads as part of the same brand surface as the
  // header. WCAG-safe foreground fallback if the configured combo would
  // be illegible (see deriveLiveCtaColors).
  const liveCtaColors = deriveLiveCtaColors(themeColor, headerFontColor);

  // Resolve agent avatar (mirrors widget): preset:* keyword → inline SVG data URI,
  // explicit URL → use as-is, otherwise fall back to bot avatar
  const agentAvatar =
    buildAgentAvatar(live.liveSettings.agent_avatar_url, live.liveSettings.agent_avatar_color)
    || avatar;

  // Status-timeline accent as an "r, g, b" triplet for the CSS var
  // --chat-status-rgb (rgba() shadows + text gradients need raw channels).
  const _statusRgbObj = _hexToRgb(themeColor) || { r: 86, g: 22, b: 234 };
  const statusAccentRgb = `${_statusRgbObj.r}, ${_statusRgbObj.g}, ${_statusRgbObj.b}`;

  let predefinedQuestions = config.predefined_questions ?? [];
  try {
    if (typeof predefinedQuestions === 'string') predefinedQuestions = JSON.parse(predefinedQuestions);
  } catch {}
  const hasRealQuestions =
    Array.isArray(predefinedQuestions) &&
    (predefinedQuestions.length > 1 || (predefinedQuestions.length === 1 && predefinedQuestions[0]?.trim() !== ''));

  // The input field; with voice input it shares one frame with the microphone (Chat.css, "VOICE INPUT"), so the
  // microphone sits inside the field on the right and a long text scrolls left of it.
  const inputField = (
    <textarea
      ref={(el) => {
        chatInputRef.current = el;
        if (el) {
          el.style.height = 'auto';
          const lineHeight = 22;
          const maxLines = 5;
          const padding = 12;
          const maxHeight = (lineHeight * maxLines) + padding;
          const minHeight = lineHeight + padding;
          const newHeight = el.value ? Math.min(el.scrollHeight, maxHeight) : minHeight;
          el.style.height = newHeight + 'px';
          if (el.scrollHeight > maxHeight) el.classList.add('has-overflow');
          else el.classList.remove('has-overflow');
        }
      }}
      rows={1}
      className="chat-input"
      value={userInput}
      onChange={onInputChange}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !e.shiftKey && !isBusy) {
          e.preventDefault();
          sendMessage();
        }
      }}
      placeholder={voice.notice || inputPlaceholder}
      disabled={isBusy}
    />
  );

  return (
    <div className="chat-fullscreen-root" style={{ fontFamily }}>
      <style>{`
        .chat-header { background-color: ${themeColor}; }
        .chat-header-title { color: ${headerFontColor}; }
        .chat-ellipsis-btn svg { fill: ${headerFontColor}; }
        .chat-send-btn { background-color: ${themeColor}; }
        .chat-send-btn:hover { background-color: ${hoverColor}; }
        .chat-send-btn svg { fill: ${headerFontColor}; }
        .chat-voice-done { background-color: ${themeColor}; color: ${headerFontColor}; }
        .chat-user-message { background-color: ${themeColor}; color: ${headerFontColor}; }
        .chat-loading-dots div { background-color: ${themeColor}; }
        .chat-agent-bar { background-color: ${themeColor}; color: ${headerFontColor}; }
        .chat-ai-disclosure-icon { fill: ${deriveDisclosureIconColor(themeColor)}; }
        .chat-live-cta-btn {
          color: ${liveCtaColors.fg};
          background: ${liveCtaColors.bg};
          border-color: ${liveCtaColors.border};
        }
        .chat-live-cta-btn:hover:not(.is-disabled) {
          background: ${liveCtaColors.hoverBg};
          border-color: ${liveCtaColors.hoverBg};
        }
        .chat-live-cta-btn:active:not(.is-disabled) {
          background: ${liveCtaColors.activeBg};
          border-color: ${liveCtaColors.activeBg};
        }
        .chat-window { --chat-status-rgb: ${statusAccentRgb}; }
      `}</style>

      <div className="chat-window">
        {/* Header */}
        <div className="chat-header">
          <div className="chat-header-content">
            <div className="chat-header-left">
              {headerIcon && (
                <img src={headerIcon} alt="Chat Logo" className="chat-header-logo" />
              )}
              <span className="chat-header-title">{headerText}</span>
            </div>
            <div className="chat-header-actions" ref={menuRef}>
              <button
                className="chat-ellipsis-btn"
                onClick={() => setMenuOpen(!menuOpen)}
                aria-label="More actions"
                title="More"
              >
                <svg viewBox="0 0 448 512">
                  <path d="M120 256a56 56 0 1 1-112 0 56 56 0 1 1 112 0zm160 0a56 56 0 1 1-112 0 56 56 0 1 1 112 0zm160 0a56 56 0 1 1-112 0 56 56 0 1 1 112 0z" />
                </svg>
              </button>
              <div className={`chat-menu ${menuOpen ? 'is-open' : ''}`} role="menu">
                <button className="chat-menu-item" onClick={clearChat} role="menuitem">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="16" height="16" fill="currentColor" aria-hidden="true" focusable="false">
                    <path d="M135.2 17.7c2.9-10.7 12.7-17.7 23.8-17.7h129.9c11.1 0 20.9 7.1 23.8 17.7L328 32H432c8.8 0 16 7.2 16 16s-7.2 16-16 16H416 32 16C7.2 64 0 56.8 0 48S7.2 32 16 32H120l15.2-14.3zM64 96H384l-21.2 355.9c-1.5 25.1-22.3 44.1-47.4 44.1H132.6c-25.1 0-45.9-19-47.4-44.1L64 96z" />
                  </svg>
                  <span className="chat-menu-item-label">Clear chat</span>
                </button>
                {liveCtaVisible && (
                  <button
                    className={`chat-menu-item${liveButtonDisabled ? ' is-disabled' : ''}`}
                    onClick={onLiveButtonClick}
                    role="menuitem"
                    disabled={liveButtonDisabled}
                  >
                    {HEADSET_SVG}
                    <span className="chat-menu-item-label">{liveButtonLabel}</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* AI disclosure strip (EU AI Act Art. 50(1)). Hidden while a human
            agent is in the chat, where "you are chatting with an AI" would be
            wrong; returns when the agent leaves unless the visitor closed it. */}
        {!aiNoticeDismissed && live.liveSessionStatus !== 'agent_joined' && (
          <div className="chat-ai-disclosure">
            <svg className="chat-ai-disclosure-icon" viewBox="0 0 512 512" aria-hidden="true">
              <path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM216 336l24 0 0-64-24 0c-13.3 0-24-10.7-24-24s10.7-24 24-24l48 0c13.3 0 24 10.7 24 24l0 88 8 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-80 0c-13.3 0-24-10.7-24-24s10.7-24 24-24zm40-208a32 32 0 1 1 0 64 32 32 0 1 1 0-64z" />
            </svg>
            <span className="chat-ai-disclosure-text">{resolveDisclosureText()}</span>
            <button
              className="chat-ai-disclosure-dismiss"
              type="button"
              onClick={() => setAiNoticeDismissed(true)}
              aria-label="Dismiss notice"
              title="Dismiss"
            >
              <svg viewBox="32 96 320 320" aria-hidden="true">
                <path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z" />
              </svg>
            </button>
          </div>
        )}

        {/* Agent-connected bar */}
        {live.liveSessionStatus === 'agent_joined' && (
          <div className="chat-agent-bar is-visible">
            <span className="chat-agent-bar-dot" aria-hidden="true"></span>
            <span className="chat-agent-bar-text">
              <span className="chat-agent-bar-name">{live.effectiveAgentDisplayName('A live agent')}</span>
              <span className="chat-agent-bar-status"> is connected</span>
            </span>
          </div>
        )}

        {/* Chat Body */}
        <div className="chat-body-wrap">
        <div className="chat-body" ref={chatBodyRef} onScroll={handleBodyScroll}>
          {messages.map((msg, idx) => {
            // System notice
            if (msg.kind === 'system') {
              return (
                <div key={idx} className="chat-system-notice">
                  <span className="chat-system-notice-text">
                    {msg.text}
                    {msg.waiting && (
                      <>
                        <span className="chat-waiting-dot"></span>
                        <span className="chat-waiting-dot"></span>
                        <span className="chat-waiting-dot"></span>
                      </>
                    )}
                  </span>
                  {msg.waiting && live.liveSessionStatus === 'agent_requested' && (
                    <button className="chat-cancel-request" onClick={onCancelWaiting}>Cancel</button>
                  )}
                </div>
              );
            }

            // Bot / Agent / User message
            const isBotLike = msg.sender === 'bot' || msg.sender === 'agent';
            const prev = messages[idx - 1];
            const showAvatar = isBotLike && (idx === 0 || prev?.sender !== msg.sender || prev?.kind === 'system');
            const hideAvatar = isBotLike && idx > 0 && prev?.sender === msg.sender && prev?.kind !== 'system';
            const avatarUrl = msg.sender === 'agent' ? agentAvatar : avatar;
            const bubbleClass = msg.sender === 'user'
              ? 'chat-message chat-user-message'
              : msg.sender === 'agent'
              ? 'chat-message chat-agent-message'
              : 'chat-message chat-bot-message';

            if (msg.sender === 'user') {
              return (
                <div key={idx} className="chat-message-row user">
                  <div className="chat-user-cluster">
                    <div
                      className={bubbleClass}
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(msg.text) }}
                    />
                    {msg.deliveryStatus && (
                      <span className={`chat-msg-tick${msg.deliveryStatus === 'read' ? ' read' : ''}`}>
                        {msg.deliveryStatus === 'read' ? '\u2713\u2713' : '\u2713'}
                      </span>
                    )}
                  </div>
                </div>
              );
            }

            return (
              <div key={idx} className={`chat-message-row ${msg.sender}`}>
                {isBotLike && avatarUrl && (
                  <div
                    className="chat-message-avatar"
                    style={{
                      backgroundImage: showAvatar ? `url("${avatarUrl}")` : 'none',
                      visibility: hideAvatar ? 'hidden' : 'visible',
                    }}
                  />
                )}
                <div
                  className={bubbleClass}
                  dangerouslySetInnerHTML={{
                    __html: isBotLike ? parseMarkdown(msg.text) : sanitizeHtml(msg.text),
                  }}
                />
                {/* In-chat product gallery: structured cards the agent attached
                    to this reply, rendered on its own full-width line below. */}
                {msg.sender === 'bot' && Array.isArray(msg.products) && msg.products.length > 0 && (
                  <div className={`ub-pc-inline${avatarUrl ? ' ub-pc-inline--indent' : ''}`}>
                    <ProductCarousel products={msg.products} accentColor={themeColor} />
                  </div>
                )}
              </div>
            );
          })}
          {agentTyping && (
            <div className="chat-agent-typing-row">
              <div className="chat-agent-typing-bubble">
                <span className="chat-typing-dot"></span>
                <span className="chat-typing-dot"></span>
                <span className="chat-typing-dot"></span>
              </div>
            </div>
          )}
          {/* Agent activity timeline: replaces the loading dots on the AI
              path — avatar + "Analyzing your question…" from the first frame
              after send (local placeholder; the server's status events upsert
              it). Rendered as a bot-message row so the answer that replaces
              it mounts an identical avatar in the identical position. */}
          {(() => {
            const displaySteps =
              statusSteps.length > 0
                ? statusSteps
                : loading
                  ? [{ key: 'analyzing', label: statusAnalyzingLabel(), phase: 'active' }]
                  : [];
            if (displaySteps.length === 0) return null;
            const timelineClass =
              'chat-status-timeline' +
              (statusCollapsing ? ' collapsing' : '');
            return (
              <div className="chat-message-row bot chat-status-message">
                {avatar && (
                  <div
                    className="chat-message-avatar"
                    style={{ backgroundImage: `url("${avatar}")` }}
                  />
                )}
                <div className={timelineClass}>
                  {displaySteps.map((s, i) => {
                    const isLast = i === displaySteps.length - 1;
                    const isActive = isLast && !statusCollapsing;
                    return (
                      <div key={s.key} className={`chat-status-step ${isActive ? 'active' : 'done'}`}>
                        <span className="chat-status-icon">
                          {isActive ? (
                            <span className="chat-status-pulse" />
                          ) : (
                            <svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true">
                              <path
                                d="M2 6.2 4.8 9 10 3.4"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </span>
                        <span className="chat-status-label">{s.label}</span>
                        {s.detail && <StatusDetail text={s.detail} />}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
          {/* Invisible spacer: keeps the pinned user message at the top while
              the reply grows; recalcSpacer shrinks it in the same layout pass
              as every content increment (the no-jump contract). */}
          <div ref={bottomSpacerRef} style={{ flexShrink: 0 }} />
          <div ref={messagesEndRef} />
        </div>
        {/* Scroll-down arrow (mirrors the widget): appears the moment content
            extends past the visible area — during streaming too — and takes
            the visitor back to the newest content. */}
        <button
          className={`chat-scroll-down-btn${showScrollDown ? ' visible' : ''}`}
          onClick={onScrollDownClick}
          aria-label="Scroll to latest"
          type="button"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" width="14" height="14">
            <path d="M169.4 470.6c12.5 12.5 32.8 12.5 45.3 0l160-160c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 370.8V64c0-17.7-14.3-32-32-32s-32 14.3-32 32v306.7L54.6 265.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160z" fill="currentColor" />
          </svg>
        </button>
        </div>

        {/* Footer */}
        <div className="chat-footer">
          {liveCtaVisible
            && (live.liveSessionStatus !== 'agent_requested')
            && (
            <div className="chat-live-cta-row">
              <button
                className={`chat-live-cta-btn${liveButtonDisabled ? ' is-disabled' : ''}`}
                type="button"
                onClick={onLiveButtonClick}
                disabled={liveButtonDisabled}
              >
                {HEADSET_SVG}
                <span className="chat-live-cta-btn-label">{liveButtonLabel}</span>
              </button>
            </div>
          )}
          {hasRealQuestions && (
            <div className="chat-predefined-container">
              {predefinedQuestions.map((q, i) => (
                <button
                  key={i}
                  className={`chat-predefined-question ${isBusy ? 'is-disabled' : ''}`}
                  onClick={() => !isBusy && sendMessage(q)}
                  disabled={isBusy}
                >
                  {q}
                </button>
              ))}
            </div>
          )}
          {!removePoweredBy && (
            <div className="chat-powered-by">
              {/* Through the counting redirect, like the widget's badge: one click per agent, then /powered-by. */}
              <a
                className="chat-powered-by-text"
                href={`${process.env.REACT_APP_API_BASE_URL}acquisition/powered-by?bot=${encodeURIComponent(botId || '')}&from=chat_link`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Powered by Ultimo Bots
              </a>
            </div>
          )}
          <div className="chat-input-container">
            {voice.state !== 'idle' ? (
              <div className={`chat-voice-bar${voice.state === 'working' ? ' is-busy' : ''}`}
                onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); voice.cancel(); } }}>
                <button className="chat-voice-cancel" type="button" onClick={voice.cancel}
                  aria-label={voice.text.cancel} title={voice.text.cancel}>
                  <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
                </button>
                <canvas className="chat-voice-wave" ref={voice.canvasRef} aria-hidden="true" />
                <span className="chat-voice-status" role="status" aria-live="polite">
                  {voice.state === 'working' ? voice.text.working : voice.text.recording}
                </span>
                <button className="chat-voice-done" type="button" onClick={voice.finish} autoFocus
                  aria-label={voice.text.done} title={voice.text.done}>
                  <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
                </button>
              </div>
            ) : (<>
            {voice.supported ? (
              <div className={`chat-input-field${isBusy ? ' is-disabled' : ''}`}>
                {inputField}
                <button className="chat-mic-btn" type="button" onClick={voice.start} disabled={isBusy}
                  aria-label={voice.text.start} title={voice.text.start}>
                  <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0" /><path d="M12 18v3" />
                  </svg>
                </button>
              </div>
            ) : inputField}
            <button
              className="chat-send-btn"
              onClick={() => sendMessage()}
              disabled={!userInput.trim() || isBusy}
              aria-label="Send message"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512">
                <path d="M214.6 41.4c-12.5-12.5-32.8-12.5-45.3 0L7 203.6c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L160 140.3V464c0 17.7 14.3 32 32 32s32-14.3 32-32V140.3l107.6 108.7c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L214.6 41.4z" />
              </svg>
            </button>
            </>)}
          </div>
        </div>

        {/* Disconnect-agent confirm overlay */}
        {showDisconnectConfirm && (
          <div className="chat-confirm-overlay">
            <div className="chat-confirm-box">
              <p>Disconnect from the live agent? Your chat will be kept.</p>
              <div className="chat-confirm-actions">
                <button className="chat-confirm-cancel" onClick={() => setShowDisconnectConfirm(false)}>Cancel</button>
                <button className="chat-confirm-ok" onClick={onConfirmDisconnect}>Disconnect</button>
              </div>
            </div>
          </div>
        )}

        {/* Clear-chat-with-live confirm overlay */}
        {showClearConfirm && (
          <div className="chat-confirm-overlay">
            <div className="chat-confirm-box">
              <p>This will disconnect the live agent. Clear chat?</p>
              <div className="chat-confirm-actions">
                <button className="chat-confirm-cancel" onClick={() => setShowClearConfirm(false)}>Cancel</button>
                <button className="chat-confirm-ok" onClick={() => { setShowClearConfirm(false); performClear(); }}>Clear</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Chat;
